import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  Link,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// Payments on the accounts this property manager is responsible for. Read-only on purpose:
// a payment is added or reversed from its account's page, so it always lands on the right account.
export default function PmPaymentsTable({ pmUuid }) {
  const [accounts, setAccounts] = useState("management");
  const [page, setPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(25);
  const [data, setData] = useState({ items: [], totalRows: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!pmUuid) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(false);
    axios
      .get(`/property-manager/${pmUuid}/payments`, { params: { accounts, page, pageLimit } })
      .then((res) => { if (!cancelled) setData(res.data || { items: [], totalRows: 0 }); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pmUuid, accounts, page, pageLimit]);

  return (
    <Box sx={{ display: "grid", gap: 1.5, p: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "70ch" }}>
          Payments on this property manager's accounts. To add or reverse a payment, open the account.
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={accounts}
          onChange={(e, v) => { if (v) { setAccounts(v); setPage(0); } }}
          aria-label="Which accounts"
        >
          <ToggleButton value="management" sx={{ textTransform: "none", px: 1.5 }}>Management accounts</ToggleButton>
          <ToggleButton value="tenants" sx={{ textTransform: "none", px: 1.5 }}>Tenants</ToggleButton>
          <ToggleButton value="all" sx={{ textTransform: "none", px: 1.5 }}>All</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error ? (
        <Alert severity="error">Payments didn't load. Switch tabs and come back to try again.</Alert>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Fee</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Posted by</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && data.items.length === 0
                  ? [0, 1, 2, 3].map((i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={7}><Skeleton variant="text" /></TableCell>
                      </TableRow>
                    ))
                  : data.items.map((p) => (
                      <TableRow key={p.uuid} hover sx={{ opacity: loading ? 0.5 : 1 }}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{moment(p.paymentDate).format("MMM D, YYYY")}</TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          <Link component={RouterLink} to={`/accountmanager/${p.accountNum}`} underline="hover" sx={{ fontWeight: 500 }}>
                            #{p.accountNum}
                          </Link>
                          <Typography variant="caption" color="text.secondary" component="div">
                            {p.accountName || "No name"}{p.customerType ? ` · ${p.customerType}` : ""}
                          </Typography>
                        </TableCell>
                        <TableCell>{p.paymentMethodTypeDesc || "Unknown"}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{money(p.paymentAmount)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{money(p.feeAmount)}</TableCell>
                        <TableCell>
                          {Number(p.reversed) === 1
                            ? <Chip label="Reversed" size="small" color="warning" variant="outlined" sx={{ height: 22 }} />
                            : <Chip label="Applied" size="small" variant="outlined" sx={{ height: 22 }} />}
                        </TableCell>
                        <TableCell>{p.postedBy || "System"}</TableCell>
                      </TableRow>
                    ))}
                {!loading && data.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary">
                        {accounts === "management" ? "No payments on this property manager's own accounts." : "No payments found."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={data.totalRows || 0}
            page={page}
            rowsPerPage={pageLimit}
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setPageLimit(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </Paper>
      )}
    </Box>
  );
}
